import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IDog, IDogRun, toPostRunSummary } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import Button from "src/components/Button/Button";
import TextArea from "src/components/TextArea/TextArea";
import RunSummary from "../Feed/FeedItem/RunSummary";
import { notifyPostCreated, showPostError } from "../postFeedback";

// Same visual language as the photo/video composer; only the run block differs.
import styles from "../MomentComposer/MomentComposer.module.scss";
import ownStyles from "./RunShareComposer.module.scss";

interface IProps {
    dog: IDog;
    /** The run that was just saved; shown as a preview of what the post will say. */
    run: Partial<IDogRun>;
    onClose: () => void;
    onPosted: () => void;
}

/**
 * Shown right after a run is logged from the home feed. The run itself is
 * already saved; this only decides whether (and with what photo/caption) it
 * goes on the feed. "Skip" keeps the run and posts nothing.
 */
const RunShareComposer: React.FC<IProps> = ({ dog, run, onClose, onPosted }) => {
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>("");
    const [caption, setCaption] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const summary = toPostRunSummary(run);

    const handlePost = async () => {
        if (!run._id) {
            onClose();
            return;
        }

        setSubmitting(true);
        try {
            let media: { type: "image"; url: string }[] | undefined;
            if (file) {
                const uploaded: any = await apiService.uploadFile(file);
                const url = typeof uploaded === "string" ? uploaded : uploaded?.value;
                if (typeof url !== "string" || !url) {
                    throw new Error("Upload did not return a file URL");
                }
                media = [{ type: Constants.post_media_type.image, url }];
            }

            const created = await apiService.createPost({
                post_type: Constants.post_type.run_result,
                dog_id: dog._id,
                run_result_id: run._id,
                body: caption.trim() || undefined,
                media,
            });
            notifyPostCreated(created);
            onPosted();
            onClose();
        } catch (err) {
            showPostError(err, {
                title: "Share failed",
                content: "Your run was saved, but we couldn't post it. Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.MomentComposer}>
            <div className={styles.title}>Share this run</div>

            <div className={styles.posterRow}>
                <div className={styles.avatar}>
                    {dog.image_url
                        ? <img src={dog.image_url} alt={dog.call_name} />
                        : <span>{dog.call_name.charAt(0).toUpperCase()}</span>}
                </div>
                <div className={styles.posterText}>
                    <div className={styles.posterName}>{dog.call_name}</div>
                    <div className={styles.posterSub}>{`Posting to ${dog.call_name}’s followers`}</div>
                </div>
            </div>

            <RunSummary run={summary} />

            <label className={styles.uploadArea}>
                <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                {previewUrl ? (
                    <img src={previewUrl} alt="" className={styles.preview} />
                ) : (
                    <>
                        <div className={styles.uploadIcon}>↑</div>
                        <div className={styles.uploadTitle}>Add a photo (optional)</div>
                        <div className={styles.uploadHint}>JPG or PNG up to 20 MB</div>
                        <span className={styles.chooseFileBtn}>Choose file</span>
                    </>
                )}
            </label>

            {file && (
                <button
                    type="button"
                    className={ownStyles.removePhoto}
                    onClick={() => setFile(null)}
                    disabled={submitting}
                >
                    Remove photo
                </button>
            )}

            <TextArea
                label="Caption"
                placeholder="Say something about this run…"
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 2000))}
                className={styles.captionField}
            />

            <div className={styles.footer}>
                <div className={styles.footerActions}>
                    <Button variant="secondary" onClick={onClose} disabled={submitting}>Skip</Button>
                    <Button onClick={handlePost} isLoading={submitting}>Post</Button>
                </div>
            </div>
        </div>
    );
};

export default RunShareComposer;
